import type { RoundAggregate } from "@engine/round/aggregate";
import type Matrix from "algorithm/matrix";
import { capPreferenceList } from "./trancheListToAlgoInterface";
import { singleLayerSProcess } from "algorithm/singleLayerSProcess";
import Vector from "algorithm/vector";
import {
  getRecommenderLayerInputUncached,
  type RecommenderLayerInput,
  type RecommenderLayerInputWithoutPreferenceLists,
} from "./recommenderPreferences";
import type {
  RecommenderBudgetPortion,
  RecommenderBudgetPortionSet,
} from "./recommenderBudgetPortions";
import {
  allocateEligibleFunding,
  FUNDING_FLOW_TOLERANCE,
  capGuaranteeForAccount,
} from "./eligibleFunding";

export type RecommenderLayerResult = {
  recommenderAccountsWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuaranteesWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  recommenderAccounts: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuarantees: Matrix<"evaluator", "evaluable+hold">;
  phase2RecommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists;
  remainingBudget: number;
};

/** Seeds are only for display what-ifs. Omit each phase's seed when its budget
 * delta is within the solver tolerance, otherwise it can mask the change. */
export type RecommenderLayerWarmStart = {
  standardGuarantees?: Matrix<"evaluator", "evaluable+hold">;
  matchingGuarantees?: Matrix<"evaluator", "evaluable+hold">;
};

/** First allocate the original portfolio. Only an infeasible payer assignment
 * introduces caps. A deficient set of grants shares its eligible capacity in
 * its current proportions; rerun the existing allocator to spend the remainder.
 * Caps apply to endorsement levels, so invert the actual sharing cost before
 * limiting a recommender. Recheck every pool because a rerun changes sharing.
 */
function tightenFundingCaps(
  output: ReturnType<typeof singleLayerSProcess>,
  budgets: Vector<"evaluator">,
  portions: RecommenderBudgetPortion[],
  proposalIds: string[],
  holdIndex: number,
  caps: number[][],
): boolean {
  let changed = false;
  let hasUnfundedGrants = false;
  for (let r = 0; r < portions.length; r++) {
    const funding = portions[r]!.funding;
    if (!funding?.some((f) => f.budget > 0 && f.excludedProposalIds.length > 0))
      continue;
    // Zero eligible capacity is an exact cap, so apply all such exclusions
    // together instead of learning one singleton cut per allocation rerun.
    for (let p = 0; p < proposalIds.length; p++) {
      if (
        p === holdIndex ||
        output.accounts.get(r, p) <= FUNDING_FLOW_TOLERANCE
      )
        continue;
      if (
        !funding.some(
          (f) =>
            f.budget > 0 && !f.excludedProposalIds.includes(proposalIds[p]!),
        ) &&
        caps[r]![p]! > 0
      ) {
        caps[r]![p] = 0;
        changed = true;
      }
    }
    const assignment = allocateEligibleFunding({
      supplies: funding.map((f) => f.budget),
      demands: output.accounts.getRow(r).toArray(),
      eligible: funding.map((f) =>
        proposalIds.map(
          (id, p) => p === holdIndex || !f.excludedProposalIds.includes(id),
        ),
      ),
      holdIndex,
    });
    hasUnfundedGrants ||= assignment.unmetDemand.some(
      (amount) => amount > assignment.tolerance,
    );
    const cut = assignment.bottleneck;
    if (!cut || cut.demand - cut.capacity <= assignment.tolerance) continue;
    const scale = cut.demand > 0 ? cut.capacity / cut.demand : 0;
    for (const p of cut.proposalIndices) {
      if (p === holdIndex) continue;
      const targetAccount = output.accounts.get(r, p) * scale;
      const cap = capGuaranteeForAccount({
        targetAccount,
        evaluatorIndex: r,
        guarantees: output.guarantees.getCol(p).toArray(),
        responsibilityBudgets: budgets.toArray(),
      });
      if (cap < caps[r]![p]!) {
        caps[r]![p] = cap;
        changed = true;
      }
    }
  }
  if (hasUnfundedGrants && !changed) {
    throw new Error(
      "Eligible funder capacity remains insufficient but no grant cap could be reduced.",
    );
  }
  return changed;
}

/** Allocate each real recommender as one pool in both phases. Blocks only
 * alter the portfolio when its grants cannot be paid by eligible funders. */
export function runRecommenderLayer({
  round,
  phase1RecommenderLayerInput,
  budgetPortionSet,
  excludedProposalIds = {},
  debugLabelPrefix,
  warmStart,
  enforceFundingEligibility = true,
}: {
  round: RoundAggregate;
  phase1RecommenderLayerInput: RecommenderLayerInput;
  budgetPortionSet: RecommenderBudgetPortionSet;
  excludedProposalIds?: { [proposalId: string]: number };
  debugLabelPrefix: string;
  warmStart?: RecommenderLayerWarmStart;
  enforceFundingEligibility?: boolean;
}): RecommenderLayerResult {
  const portions = budgetPortionSet.portions;
  const proposalIds = phase1RecommenderLayerInput.orderedProposals.map(
    (p) => p.id,
  );
  const holdIndex = proposalIds.indexOf(round.holdProposalId);
  const budgets = new Vector<"evaluator">(portions.map((p) => p.budget));
  const standardBudgets = new Vector<"evaluator">(
    portions.map((p) => p.standardBudget),
  );
  const caps = portions.map(() => proposalIds.map(() => Infinity));
  const hasRestrictions = portions.some((p) =>
    p.funding?.some((f) => f.budget > 0 && f.excludedProposalIds.length > 0),
  );
  // Phase one is a preference benchmark, not a payment assignment. First solve
  // both phases without blocks. Only an infeasible FINAL portfolio adds caps;
  // then rerun both phases so freed standard money can follow its preferences.
  // A matching-only eligible funder must not distort an affordable portfolio
  // merely because it did not contribute to the standard benchmark.
  // Allow gradual sharing convergence as well as the number of cap constraints.
  for (
    let iteration = 0;
    iteration < Math.max(256, portions.length * proposalIds.length + 1);
    iteration++
  ) {
    const applyCaps = (lists: Matrix<"tranche", "proposal+hold">[]) =>
      lists.map((p, r) => {
        const preference = p.rename(
          "tranche -> tranche",
          "proposal+hold -> evaluable+hold",
        );
        return iteration === 0
          ? preference
          : capPreferenceList(preference, caps[r]!);
      });
    const standard = singleLayerSProcess(
      standardBudgets,
      applyCaps(phase1RecommenderLayerInput.recommenderPreferenceLists),
      {
        debugLabel: `${debugLabelPrefix}:phase1`,
        initialGuarantees: !hasRestrictions
          ? warmStart?.standardGuarantees
          : undefined,
      },
    );
    const phase2RecommenderLayerInput = getRecommenderLayerInputUncached(
      round,
      excludedProposalIds,
      {
        kind: "matching",
        initialGuarantees: standard.guarantees.rename(
          "evaluator -> recommender",
          "evaluable+hold -> evaluable+hold",
        ),
      },
    );
    const matching = singleLayerSProcess(
      budgets,
      applyCaps(phase2RecommenderLayerInput.recommenderPreferenceLists),
      {
        debugLabel: `${debugLabelPrefix}:phase2`,
        initialGuarantees: !hasRestrictions
          ? warmStart?.matchingGuarantees
          : undefined,
      },
    );
    if (
      enforceFundingEligibility &&
      tightenFundingCaps(
        matching,
        budgets,
        portions,
        proposalIds,
        holdIndex,
        caps,
      )
    )
      continue;
    return {
      recommenderAccountsWithoutMatching: standard.accounts,
      recommenderGuaranteesWithoutMatching: standard.guarantees,
      recommenderAccounts: matching.accounts,
      recommenderGuarantees: matching.guarantees,
      phase2RecommenderLayerInput,
      remainingBudget: matching.remainingBudget,
    };
  }
  // Never return an attribution that overspends an eligible funder if
  // interacting caps fail to settle within the numerical guard.
  throw new Error(
    "Eligible funder capacity did not converge while capping recommender grants.",
  );
}
